// ============================================================
// Job Detail Page — Pipeline, Candidates, Forms, Settings
// ============================================================

import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
    ArrowRight, FileText, ClipboardCheck, Users, BarChart3,
    Plus, Trash2, GripVertical, ChevronRight, Copy, ExternalLink,
    Edit2, X, Settings, Save, Search, ChevronDown, ArrowUpDown,
    Share2, Eye, Download
} from 'lucide-react';
import toast from 'react-hot-toast';
import { generateAllCandidatesPDF } from '../utils/pdfGenerator';
import { useState } from 'react';

export function JobDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState('candidates');

    const { data: jobData, isLoading } = useQuery({
        queryKey: ['job', id],
        queryFn: () => api.get(`/jobs/${id}`),
    });

    const { data: candidatesData } = useQuery({
        queryKey: ['job-candidates', id],
        queryFn: () => api.get(`/jobs/${id}/candidates`),
    });

    const { data: formsData } = useQuery({
        queryKey: ['forms'],
        queryFn: () => api.get('/forms'),
    });

    const updateJobMutation = useMutation({
        mutationFn: (body: any) => api.put(`/jobs/${id}`, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['job', id] });
            toast.success('Job updated');
        },
        onError: (err: any) => toast.error(err.message),
    });

    const deleteJobMutation = useMutation({
        mutationFn: () => api.delete(`/jobs/${id}`),
        onSuccess: () => {
            toast.success('Job deleted');
            navigate('/jobs');
        },
        onError: (err: any) => toast.error(err.message),
    });

    const job = jobData?.data;
    const candidates = candidatesData?.data || [];
    const pagination = candidatesData?.pagination;
    const stages = job?.stages || [];

    if (isLoading || !job) return <div className="loading-page"><div className="loading-spinner" /></div>;

    const tabs = [
        { id: 'candidates', label: 'Candidates', icon: <Users size={16} />, count: pagination?.total || 0 },
        { id: 'pipeline', label: 'Pipeline Stages', icon: <ArrowUpDown size={16} /> },
        { id: 'forms', label: 'Application Form', icon: <FileText size={16} /> },
        { id: 'settings', label: 'Settings', icon: <Settings size={16} /> },
    ];

    return (
        <div className="animate-fadeIn">
            <div className="page-header">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <Link to="/jobs" style={{ color: '#6b7280', fontSize: '14px' }}>← Jobs</Link>
                    </div>
                    <h1>{job.title}</h1>
                    <p className="subtitle">
                        <span className={`badge badge-${job.status === 'active' ? 'success' : job.status === 'draft' ? 'warning' : 'info'} mr-2`}>{job.status}</span>
                        {job.department && <span className="badge badge-accent mr-2">{job.department}</span>}
                        {job.location && <span style={{ color: '#6b7280' }}>📍 {job.location}</span>}
                    </p>
                </div>
                <div className="flex gap-3">
                    <Link to={`/jobs/${id}/leaderboard`} className="btn btn-secondary">
                        <BarChart3 size={16} /> Leaderboard
                    </Link>
                    {job.status === 'draft' && (
                        <button className="btn btn-primary" onClick={() => updateJobMutation.mutate({ status: 'active' })}>
                            Publish Job
                        </button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs">
                {tabs.map(tab => (
                    <button key={tab.id} className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}>
                        {tab.icon}
                        <span style={{ marginLeft: 4 }}>{tab.label}</span>
                        {tab.count !== undefined && <span className="badge badge-accent" style={{ fontSize: '10px', marginLeft: 6 }}>{tab.count}</span>}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'candidates' && <CandidatesTab jobId={id!} candidates={candidates} stages={stages} />}
            {activeTab === 'pipeline' && <PipelineTab jobId={id!} stages={stages} />}
            {activeTab === 'forms' && <FormsTab job={job} forms={formsData?.data || []} />}
            {activeTab === 'settings' && <SettingsTab job={job} onDelete={() => { if (confirm('Delete this job permanently?')) deleteJobMutation.mutate(); }} />}
        </div>
    );
}

// ── Candidates Tab ───────────────────────────────────────────

function CandidatesTab({ jobId, candidates, stages }: { jobId: string; candidates: any[]; stages: any[] }) {
    const queryClient = useQueryClient();
    const [searchFilter, setSearchFilter] = useState('');
    const [stageFilter, setStageFilter] = useState('');

    const moveStageMutation = useMutation({
        mutationFn: ({ candidateId, stageId }: { candidateId: string; stageId: string }) =>
            api.post(`/jobs/${jobId}/candidates/${candidateId}/move-stage`, { stageId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['job-candidates', jobId] });
            toast.success('Stage updated');
        },
        onError: (err: any) => toast.error(err.message),
    });

    const filtered = candidates.filter(c => {
        const matchSearch = !searchFilter ||
            c.candidate.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
            c.candidate.email.toLowerCase().includes(searchFilter.toLowerCase());
        const matchStage = !stageFilter || c.stage?.id === stageFilter;
        return matchSearch && matchStage;
    });

    return (
        <div className="card">
            {/* Filters */}
            <div className="flex gap-4 mb-4" style={{ flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
                    <input className="form-input" placeholder="Search candidates..."
                        value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)}
                        style={{ paddingLeft: 36 }} />
                </div>
                {stages.length > 0 && (
                    <select className="form-select" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
                        style={{ width: 180 }}>
                        <option value="">All Stages</option>
                        {stages.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                )}
                <button
                    className="btn btn-primary btn-sm"
                    style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
                    onClick={() => {
                        try {
                            const pdfData = filtered.map(c => ({
                                candidateName: c.candidate?.name || 'Unknown',
                                name: c.candidate?.name || 'Unknown',
                                email: c.candidate?.email || '',
                                status: c.stage?.name || 'Unknown',
                                scorePercent: null,
                                completedAt: c.record?.appliedAt,
                                responses: [],
                            }));
                            generateAllCandidatesPDF(pdfData, 'Candidates List');
                            toast.success('PDF downloaded');
                        } catch (err) {
                            toast.error('Failed to generate PDF');
                        }
                    }}
                >
                    <Download size={14} /> Download All (PDF)
                </button>
            </div>

            {filtered.length === 0 ? (
                <div className="empty-state">
                    <div className="icon">👤</div>
                    <h3>No candidates {candidates.length > 0 ? 'matching filter' : 'yet'}</h3>
                    <p>{candidates.length > 0 ? 'Try changing your filters' : 'Share the application form link to start receiving candidates'}</p>
                </div>
            ) : (
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Candidate</th>
                            <th>Current Stage</th>
                            <th>Applied</th>
                            <th>Tags</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((c: any) => (
                            <tr key={c.record.id}>
                                <td>
                                    <div>
                                        <div style={{ fontWeight: 600 }}>{c.candidate.name}</div>
                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{c.candidate.email}</div>
                                    </div>
                                </td>
                                <td>
                                    {stages.length > 0 ? (
                                        <select className="form-select" value={c.stage?.id || ''}
                                            onChange={(e) => moveStageMutation.mutate({
                                                candidateId: c.record.id,
                                                stageId: e.target.value,
                                            })}
                                            style={{
                                                width: 160, fontSize: '12px', padding: '4px 8px',
                                                background: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)',
                                                borderRadius: 8, color: 'var(--color-text-primary)'
                                            }}>
                                            {stages.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        </select>
                                    ) : (
                                        <span className="badge badge-accent">{c.stage?.name || '—'}</span>
                                    )}
                                </td>
                                <td style={{ color: '#6b7280', fontSize: '13px' }}>
                                    {new Date(c.record.appliedAt).toLocaleDateString()}
                                </td>
                                <td>
                                    {(c.record.tags || []).map((tag: string) => (
                                        <span key={tag} className="badge badge-info mr-2" style={{ marginBottom: 2 }}>{tag}</span>
                                    ))}
                                </td>
                                <td>
                                    <Link to={`/candidates/${c.record.id}`} className="btn btn-ghost btn-sm">
                                        Profile <ArrowRight size={14} />
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

// ── Pipeline Stages Tab ──────────────────────────────────────

function PipelineTab({ jobId, stages: initialStages }: { jobId: string; stages: any[] }) {
    const queryClient = useQueryClient();
    const [stages, setStages] = useState(initialStages.map((s, i) => ({ ...s, order: s.order ?? i })));
    const [hasChanges, setHasChanges] = useState(false);

    const saveMutation = useMutation({
        mutationFn: (stageData: any[]) => api.put(`/jobs/${jobId}/stages`, { stages: stageData }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['job', jobId] });
            setHasChanges(false);
            toast.success('Pipeline stages saved');
        },
        onError: (err: any) => toast.error(err.message),
    });

    const addStage = () => {
        setStages([...stages, { id: `new-${Date.now()}`, name: 'New Stage', order: stages.length }]);
        setHasChanges(true);
    };

    const removeStage = (index: number) => {
        setStages(stages.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i })));
        setHasChanges(true);
    };

    const renameStage = (index: number, name: string) => {
        const updated = [...stages];
        updated[index] = { ...updated[index], name };
        setStages(updated);
        setHasChanges(true);
    };

    const moveStage = (from: number, direction: 'up' | 'down') => {
        const to = direction === 'up' ? from - 1 : from + 1;
        if (to < 0 || to >= stages.length) return;
        const updated = [...stages];
        [updated[from], updated[to]] = [updated[to], updated[from]];
        setStages(updated.map((s, i) => ({ ...s, order: i })));
        setHasChanges(true);
    };

    const handleSave = () => {
        saveMutation.mutate(stages.map((s, i) => ({
            id: s.id.startsWith('new-') ? undefined : s.id,
            name: s.name,
            order: i,
        })));
    };

    return (
        <div className="card">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h3 style={{ fontWeight: 700, marginBottom: 4 }}>Hiring Pipeline</h3>
                    <p style={{ color: '#6b7280', fontSize: '13px' }}>Define the stages candidates move through</p>
                </div>
                <div className="flex gap-3">
                    <button className="btn btn-ghost" onClick={addStage}>
                        <Plus size={16} /> Add Stage
                    </button>
                    {hasChanges && (
                        <button className="btn btn-primary" onClick={handleSave} disabled={saveMutation.isPending}>
                            <Save size={16} /> {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
                        </button>
                    )}
                </div>
            </div>

            {stages.length === 0 ? (
                <div className="empty-state">
                    <div className="icon">📋</div>
                    <h3>No stages defined</h3>
                    <p>Add stages like "Applied", "Screening", "Interview", "Assessment", "Hired"</p>
                    <button className="btn btn-primary mt-4" onClick={addStage}>
                        <Plus size={16} /> Add First Stage
                    </button>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {stages.map((stage, i) => (
                        <div key={stage.id} style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '12px 16px', background: 'var(--color-bg-tertiary)',
                            borderRadius: 10, border: '1px solid var(--color-border)',
                        }}>
                            <div className="flex flex-col" style={{ gap: 2 }}>
                                <button className="btn btn-ghost" onClick={() => moveStage(i, 'up')} disabled={i === 0}
                                    style={{ padding: 2, opacity: i === 0 ? 0.3 : 1 }}>
                                    <ChevronDown size={14} style={{ transform: 'rotate(180deg)' }} />
                                </button>
                                <button className="btn btn-ghost" onClick={() => moveStage(i, 'down')} disabled={i === stages.length - 1}
                                    style={{ padding: 2, opacity: i === stages.length - 1 ? 0.3 : 1 }}>
                                    <ChevronDown size={14} />
                                </button>
                            </div>

                            <span style={{
                                width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1', fontWeight: 700, fontSize: '12px',
                            }}>
                                {i + 1}
                            </span>

                            <input
                                value={stage.name}
                                onChange={(e) => renameStage(i, e.target.value)}
                                style={{
                                    flex: 1, background: 'transparent', border: 'none', outline: 'none',
                                    fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)',
                                }}
                            />

                            {i > 0 && (
                                <ChevronRight size={14} color="#6b7280" style={{ opacity: 0.3 }} />
                            )}

                            <button className="btn btn-ghost btn-sm" onClick={() => removeStage(i)}
                                style={{ color: '#ef4444', padding: 4 }}>
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Forms Tab ────────────────────────────────────────────────

function FormsTab({ job, forms }: { job: any; forms: any[] }) {
    const queryClient = useQueryClient();
    const [showCreate, setShowCreate] = useState(false);
    const [formName, setFormName] = useState('');

    const linkFormMutation = useMutation({
        mutationFn: (formId: string) => api.put(`/jobs/${job.id}`, { formId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['job', job.id] });
            toast.success('Form linked to job');
        },
        onError: (err: any) => toast.error(err.message),
    });

    const createFormMutation = useMutation({
        mutationFn: (name: string) => api.post('/forms', { name }),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['forms'] });
            linkFormMutation.mutate(data.data.id);
            setShowCreate(false);
            setFormName('');
        },
        onError: (err: any) => toast.error(err.message),
    });

    const linkedForm = forms.find(f => f.id === job.formId);

    return (
        <div className="card">
            <div className="flex justify-between items-center mb-4">
                <div>
                    <h3 style={{ fontWeight: 700, marginBottom: 4 }}>Application Form</h3>
                    <p style={{ color: '#6b7280', fontSize: '13px' }}>The form candidates fill out to apply for this job</p>
                </div>
            </div>

            {linkedForm ? (
                <div style={{
                    padding: 20, background: 'var(--color-bg-tertiary)', borderRadius: 12,
                    border: '1px solid var(--color-border)',
                }}>
                    <div className="flex justify-between items-center mb-3">
                        <div>
                            <h4 style={{ fontWeight: 700, fontSize: '15px' }}>{linkedForm.name}</h4>
                            <span className={`badge badge-${linkedForm.status === 'published' ? 'success' : 'warning'} mt-2`}>
                                {linkedForm.status}
                            </span>
                        </div>
                        <div className="flex gap-2">
                            <Link to={`/forms/${linkedForm.id}/builder`} className="btn btn-secondary btn-sm">
                                <Edit2 size={14} /> Edit Form
                            </Link>
                        </div>
                    </div>

                    {linkedForm.publicToken && (
                        <div style={{
                            marginTop: 12, padding: '10px 14px', background: 'var(--color-bg-card)',
                            borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10,
                        }}>
                            <Share2 size={14} color="#6366f1" />
                            <span style={{ fontSize: '13px', color: '#6b7280', flex: 1 }}>
                                {window.location.origin}/apply/{linkedForm.publicToken}
                            </span>
                            <button className="btn btn-ghost btn-sm" onClick={() => {
                                navigator.clipboard.writeText(`${window.location.origin}/apply/${linkedForm.publicToken}`);
                                toast.success('Link copied!');
                            }}>
                                <Copy size={14} /> Copy
                            </button>
                            <a href={`/apply/${linkedForm.publicToken}`} target="_blank" className="btn btn-ghost btn-sm">
                                <ExternalLink size={14} />
                            </a>
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Link existing form */}
                    {forms.length > 0 && (
                        <div>
                            <label className="form-label">Link an existing form</label>
                            <div className="flex gap-3">
                                <select className="form-select" style={{ flex: 1 }}
                                    onChange={(e) => { if (e.target.value) linkFormMutation.mutate(e.target.value); }}>
                                    <option value="">Select a form...</option>
                                    {forms.map(f => <option key={f.id} value={f.id}>{f.name} ({f.status})</option>)}
                                </select>
                            </div>
                        </div>
                    )}

                    <div style={{ textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                        {forms.length > 0 ? '— or —' : ''}
                    </div>

                    {/* Create new form */}
                    {showCreate ? (
                        <div className="flex gap-3">
                            <input className="form-input" value={formName}
                                onChange={(e) => setFormName(e.target.value)}
                                placeholder="Form name (e.g. Software Engineer Application)"
                                style={{ flex: 1 }} />
                            <button className="btn btn-primary" onClick={() => formName && createFormMutation.mutate(formName)}
                                disabled={!formName || createFormMutation.isPending}>
                                {createFormMutation.isPending ? 'Creating...' : 'Create & Link'}
                            </button>
                            <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                        </div>
                    ) : (
                        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                            <Plus size={16} /> Create New Form
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Settings Tab ─────────────────────────────────────────────

function SettingsTab({ job, onDelete }: { job: any; onDelete: () => void }) {
    const queryClient = useQueryClient();
    const [title, setTitle] = useState(job.title);
    const [description, setDescription] = useState(job.description || '');
    const [status, setStatus] = useState(job.status);

    const updateMutation = useMutation({
        mutationFn: (body: any) => api.put(`/jobs/${job.id}`, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['job', job.id] });
            queryClient.invalidateQueries({ queryKey: ['jobs'] });
            toast.success('Job settings saved');
        },
        onError: (err: any) => toast.error(err.message),
    });

    return (
        <div className="card">
            <h3 style={{ fontWeight: 700, marginBottom: 20 }}>Job Settings</h3>
            <form onSubmit={(e) => { e.preventDefault(); updateMutation.mutate({ title, description, status }); }}>
                <div className="form-group">
                    <label className="form-label">Job Title</label>
                    <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
                <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea className="form-textarea" value={description} onChange={(e) => setDescription(e.target.value)} rows={5} />
                </div>
                <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 200 }}>
                        <option value="draft">Draft</option>
                        <option value="active">Active</option>
                        <option value="paused">Paused</option>
                        <option value="closed">Closed</option>
                    </select>
                </div>
                <div className="flex justify-between mt-6">
                    <button type="button" className="btn btn-ghost" onClick={onDelete}
                        style={{ color: '#ef4444' }}>
                        <Trash2 size={16} /> Delete Job
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={updateMutation.isPending}>
                        <Save size={16} /> {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
}
